import React, { useEffect, useMemo, useState } from 'react';
import AdminPageTitle from '../components/AdminPageTitle';
import RichTextEditor from '../components/RichTextEditor';
import {
  createServiceVariation,
  deleteServiceVariation,
  fetchServicePages,
  updateServicePage,
  updateServiceVariation,
} from '../services/servicePages';
import './ServicePages.css';

const emptyVariation = {
  title: '',
  content: '',
  display_order: 0,
  active: true,
};

const hasVisibleText = (html) => html
  .replace(/<[^>]*>/g, '')
  .replace(/&nbsp;/g, ' ')
  .trim().length > 0;

const ServicePages = () => {
  const [pages, setPages] = useState([]);
  const [selectedPageId, setSelectedPageId] = useState(null);
  const [pageForm, setPageForm] = useState({ title: '', summary: '', active: true });
  const [variationForm, setVariationForm] = useState(emptyVariation);
  const [editingVariation, setEditingVariation] = useState(null);
  const [showVariationModal, setShowVariationModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const selectedPage = useMemo(
    () => pages.find((page) => page.id === selectedPageId) || null,
    [pages, selectedPageId]
  );

  const loadPages = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await fetchServicePages();
      setPages(data);
      setSelectedPageId((currentId) => {
        if (data.some((page) => page.id === currentId)) return currentId;
        return data[0]?.id ?? null;
      });
    } catch (err) {
      setPages([]);
      setError(err.response?.data?.message || err.message || 'Failed to load service pages.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPages();
  }, []);

  useEffect(() => {
    if (!selectedPage) return;
    setPageForm({
      title: selectedPage.title || '',
      summary: selectedPage.summary || '',
      active: selectedPage.active !== false,
    });
  }, [selectedPage]);

  const handlePageSave = async (event) => {
    event.preventDefault();
    if (!selectedPage || !pageForm.title.trim()) return;

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await updateServicePage(selectedPage.id, {
        ...pageForm,
        title: pageForm.title.trim(),
        summary: pageForm.summary.trim(),
      });
      setSuccess('Service page updated successfully.');
      await loadPages();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update the service page.');
    } finally {
      setSaving(false);
    }
  };

  const openCreateVariation = () => {
    setEditingVariation(null);
    setVariationForm(emptyVariation);
    setError('');
    setShowVariationModal(true);
  };

  const openEditVariation = (variation) => {
    setEditingVariation(variation);
    setVariationForm({
      title: variation.title || '',
      content: variation.content || '',
      display_order: variation.display_order ?? 0,
      active: variation.active !== false,
    });
    setError('');
    setShowVariationModal(true);
  };

  const closeVariationModal = () => {
    setShowVariationModal(false);
    setEditingVariation(null);
    setVariationForm(emptyVariation);
    setError('');
  };

  const handleVariationSave = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!variationForm.title.trim() || !hasVisibleText(variationForm.content)) {
      setError('Variation title and description are required.');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...variationForm,
        title: variationForm.title.trim(),
      };
      if (editingVariation) {
        await updateServiceVariation(editingVariation.id, payload);
        setSuccess('Service variation updated successfully.');
      } else {
        await createServiceVariation(selectedPage.id, payload);
        setSuccess('Service variation added successfully.');
      }
      setShowVariationModal(false);
      setEditingVariation(null);
      setVariationForm(emptyVariation);
      await loadPages();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save the service variation.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVariation = async (variation) => {
    if (!window.confirm(`Delete the variation “${variation.title}”?`)) return;
    try {
      setError('');
      setSuccess('');
      await deleteServiceVariation(variation.id);
      setSuccess('Service variation deleted successfully.');
      await loadPages();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete the service variation.');
    }
  };

  return (
    <div className="admin-service-pages">
      <AdminPageTitle title="Service Details" />
      <div className="page-header">
        <div>
          <h1>Service Details</h1>
          <p>Manage public service pages and their individual variations or quotations.</p>
        </div>
      </div>

      {error && <div className="service-admin-alert service-admin-alert--error">{error}</div>}
      {success && <div className="service-admin-alert service-admin-alert--success">{success}</div>}

      {loading ? (
        <div className="service-admin-status">Loading service pages...</div>
      ) : !selectedPage ? (
        <div className="service-admin-status">No service pages are available.</div>
      ) : (
        <>
          {pages.length > 1 && (
            <div className="service-page-selector">
              <label htmlFor="service-page">Service</label>
              <select id="service-page" value={selectedPageId || ''} onChange={(event) => setSelectedPageId(Number(event.target.value))}>
                {pages.map((page) => <option key={page.id} value={page.id}>{page.title}</option>)}
              </select>
            </div>
          )}

          <form className="service-page-form" onSubmit={handlePageSave}>
            <h2>Page Information</h2>
            <div className="service-form-grid">
              <div className="service-form-group">
                <label htmlFor="service-title">Service Title *</label>
                <input id="service-title" value={pageForm.title} onChange={(event) => setPageForm((current) => ({ ...current, title: event.target.value }))} required />
              </div>
              <div className="service-form-group service-form-group--full">
                <label htmlFor="service-summary">Introductory Summary</label>
                <textarea id="service-summary" rows="3" value={pageForm.summary} onChange={(event) => setPageForm((current) => ({ ...current, summary: event.target.value }))} />
              </div>
              <label className="service-checkbox">
                <input type="checkbox" checked={pageForm.active} onChange={(event) => setPageForm((current) => ({ ...current, active: event.target.checked }))} />
                Publicly visible
              </label>
            </div>
            <button type="submit" className="service-primary-btn" disabled={saving}>{saving ? 'Saving...' : 'Save Page Information'}</button>
          </form>

          <section className="service-variation-management">
            <div className="service-variation-heading">
              <div>
                <h2>Variations</h2>
                <p>Add each greenhouse size, package, or quotation separately.</p>
              </div>
              <button type="button" className="service-primary-btn" onClick={openCreateVariation}>+ Add Variation</button>
            </div>

            {!selectedPage.variations?.length ? (
              <div className="service-admin-status">No variations have been added yet.</div>
            ) : (
              <div className="service-admin-variations">
                {selectedPage.variations.map((variation) => (
                  <article className="service-admin-variation" key={variation.id}>
                    <div className="service-admin-variation-copy">
                      <div className="service-variation-meta">
                        <span>Order {variation.display_order}</span>
                        <span className={variation.active ? 'is-active' : 'is-inactive'}>{variation.active ? 'Visible' : 'Hidden'}</span>
                      </div>
                      <h3>{variation.title}</h3>
                      <div className="service-admin-content-preview" dangerouslySetInnerHTML={{ __html: variation.content }} />
                    </div>
                    <div className="service-admin-actions">
                      <button type="button" className="service-edit-btn" onClick={() => openEditVariation(variation)}>Edit</button>
                      <button type="button" className="service-delete-btn" onClick={() => handleDeleteVariation(variation)}>Delete</button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {showVariationModal && (
        <div className="service-modal-overlay" onMouseDown={closeVariationModal}>
          <div className="service-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="service-modal-header">
              <h2>{editingVariation ? 'Edit Variation' : 'Add Variation'}</h2>
              <button type="button" onClick={closeVariationModal} aria-label="Close">&times;</button>
            </div>
            <form className="service-variation-form" onSubmit={handleVariationSave}>
              {error && <div className="service-admin-alert service-admin-alert--error">{error}</div>}
              <div className="service-form-group">
                <label htmlFor="variation-title">Variation Title *</label>
                <input
                  id="variation-title"
                  value={variationForm.title}
                  onChange={(event) => setVariationForm((current) => ({ ...current, title: event.target.value }))}
                  placeholder="e.g. Greenhouse 50 × 8"
                  required
                />
              </div>
              <div className="service-form-group">
                <label>Full Description / Quotation *</label>
                <RichTextEditor
                  value={variationForm.content}
                  onChange={(content) => setVariationForm((current) => ({ ...current, content }))}
                />
                <small>Use the toolbar to preserve headings, bold text, bullets, and numbering.</small>
              </div>
              <div className="service-form-row">
                <div className="service-form-group">
                  <label htmlFor="variation-order">Display Order</label>
                  <input
                    id="variation-order"
                    type="number"
                    min="0"
                    value={variationForm.display_order}
                    onChange={(event) => setVariationForm((current) => ({ ...current, display_order: parseInt(event.target.value, 10) || 0 }))}
                  />
                </div>
                <label className="service-checkbox">
                  <input
                    type="checkbox"
                    checked={variationForm.active}
                    onChange={(event) => setVariationForm((current) => ({ ...current, active: event.target.checked }))}
                  />
                  Publicly visible
                </label>
              </div>
              <div className="service-modal-actions">
                <button type="button" className="service-cancel-btn" onClick={closeVariationModal}>Cancel</button>
                <button type="submit" className="service-primary-btn" disabled={saving}>
                  {saving ? 'Saving...' : editingVariation ? 'Update Variation' : 'Add Variation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicePages;
